import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Button, IconButton } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { updateCompany } from '../../../redux/company/actions';
import { Image as ImageIcon, X } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { COMPANY_BRAND_ORANGE } from '../../../constants/companyBrand';

interface ICompanyGalleryEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const SURFACE_CARD = '#1a1a1a';
const BTN_SURFACE = '#262626';

/** Bento grid: first image 8×2, second 4×2, then rows of three 4-col tiles (matches gallery mock). */
function galleryItemGridSx(index: number, total: number): Record<string, unknown> {
    if (total <= 0) return {};
    if (total === 1) {
        return {
            gridColumn: '1 / -1',
            gridRow: { xs: 'auto', md: 'span 2' },
        };
    }
    if (index === 0) {
        return {
            gridColumn: { xs: '1 / -1', md: '1 / span 8' },
            gridRow: { xs: 'auto', md: 'span 2' },
        };
    }
    if (index === 1) {
        return {
            gridColumn: { xs: '1 / -1', md: '9 / span 4' },
            gridRow: { xs: 'auto', md: 'span 2' },
        };
    }
    const rel = index - 2;
    const row = Math.floor(rel / 3) + 3;
    const colStart = (rel % 3) * 4 + 1;
    return {
        gridColumn: { xs: '1 / -1', md: `${colStart} / span 4` },
        gridRow: { xs: 'auto', md: `${row}` },
    };
}

const CompanyGalleryEdit: React.FC<ICompanyGalleryEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState({
        gallery: company?.gallery || [],
    });
    const [initialData, setInitialData] = useState(formData);
    const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
    const [galleryImages, setGalleryImages] = useState<string[]>([]);

    useEffect(() => {
        if (company) {
            const newData = {
                gallery: company.gallery || [],
            };
            setFormData(newData);
            setInitialData(newData);
            setGalleryImages(company.gallery || []);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleSave = () => {
        if (company?._id) {
            const payload: { id: string; body: { gallery: File[] | string[] } } = {
                id: company._id,
                body: {
                    gallery: galleryFiles.length > 0 ? galleryFiles : formData.gallery,
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id, galleryFiles]);

    const compressImage = (base64: string, quality = 0.7): Promise<string> => {
        return new Promise(resolve => {
            const img = new window.Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0);
                canvas.toBlob(
                    blob => {
                        if (blob) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                resolve(reader.result as string);
                            };
                            reader.readAsDataURL(blob);
                        } else {
                            resolve(base64);
                        }
                    },
                    'image/jpeg',
                    quality,
                );
            };
            img.src = base64;
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const newFiles: File[] = [];
            const newImages: string[] = [];

            for (const file of Array.from(files)) {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    const result = await new Promise<string>(resolve => {
                        reader.onload = async e => {
                            const base64 = e.target?.result as string;
                            if (base64) {
                                const compressedBase64 = await compressImage(base64, 0.7);
                                resolve(compressedBase64);
                            }
                        };
                        reader.readAsDataURL(file);
                    });

                    if (result) {
                        const response = await fetch(result);
                        const blob = await response.blob();
                        const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });

                        newFiles.push(compressedFile);
                        newImages.push(result);
                    }
                }
            }

            if (newFiles.length > 0) {
                setGalleryFiles(prev => [...prev, ...newFiles]);
                setGalleryImages(prev => [...prev, ...newImages]);
                setFormData(prev => ({
                    ...prev,
                    gallery: [...prev.gallery, ...newImages],
                }));
            }
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleAddImages = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveImage = (imageUrl: string, index: number) => {
        setGalleryImages(prev => prev.filter((_, i) => i !== index));
        setGalleryFiles(prev => prev.filter((_, i) => i !== index));
        setFormData(prev => ({
            ...prev,
            gallery: prev.gallery.filter(url => url !== imageUrl),
        }));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { md: 'flex-end' },
                    justifyContent: 'space-between',
                    gap: 3,
                }}>
                <Box>
                    <Typography
                        component="h1"
                        sx={{
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            fontWeight: 800,
                            fontSize: { xs: '1.75rem', md: '2.25rem' },
                            color: '#ffffff',
                            letterSpacing: '-0.02em',
                            mb: 1.5,
                        }}>
                        Company{' '}
                        <Box component="span" sx={{ color: COMPANY_BRAND_ORANGE }}>
                            Gallery
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.55)',
                            maxWidth: 520,
                            lineHeight: 1.65,
                            fontSize: '0.95rem',
                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        }}>
                        Curate your production stills and studio imagery. Strong visuals help attract talent and partners.
                    </Typography>
                </Box>
                {isEditing && (
                    <Button
                        startIcon={
                            <Add
                                sx={{
                                    color: COMPANY_BRAND_ORANGE,
                                    fontSize: 18,
                                    transition: 'transform 0.25s ease',
                                }}
                            />
                        }
                        onClick={handleAddImages}
                        sx={{
                            alignSelf: { xs: 'flex-start', md: 'auto' },
                            flexShrink: 0,
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: BTN_SURFACE,
                            color: '#ffffff',
                            borderRadius: 999,
                            px: 2.5,
                            py: 0.75,
                            minHeight: 40,
                            textTransform: 'none',
                            fontSize: 12,
                            fontWeight: 700,
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            letterSpacing: '0.04em',
                            whiteSpace: 'nowrap',
                            lineHeight: 1,
                            '& .MuiButton-startIcon': {
                                marginRight: 0,
                                marginLeft: 0,
                            },
                            '&:hover': {
                                bgcolor: '#333333',
                                '& .MuiButton-startIcon .MuiSvgIcon-root': { transform: 'rotate(90deg)' },
                            },
                        }}>
                        ADD IMAGES
                    </Button>
                )}
            </Box>

            <input
                type="file"
                accept="image/*"
                multiple
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            <Box
                sx={{
                    bgcolor: SURFACE_CARD,
                    borderRadius: 3,
                    p: { xs: 2, sm: 3 },
                    border: '1px solid rgba(255,255,255,0.06)',
                }}>
                {galleryImages.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(255,255,255,0.55)' }}>
                        <ImageIcon size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            No images in gallery yet. Click &quot;Add Images&quot; to get started.
                        </Typography>
                    </Box>
                ) : (
                    <PhotoProvider>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' },
                                gap: { xs: 2, md: 3 },
                                gridAutoRows: { xs: 'minmax(200px, auto)', md: '250px' },
                            }}>
                            {galleryImages.map((imageUrl, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        position: 'relative',
                                        minHeight: { xs: 200, md: 'unset' },
                                        height: { md: '100%' },
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        backgroundColor: SURFACE_CARD,
                                        border: '1px solid rgba(255,255,255,0.06)',
                                        cursor: 'pointer',
                                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                        ...galleryItemGridSx(index, galleryImages.length),
                                        '&:hover': {
                                            borderColor: 'rgba(250,175,63,0.35)',
                                        },
                                    }}>
                                    {isEditing && (
                                        <IconButton
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleRemoveImage(imageUrl, index);
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                top: 16,
                                                right: 16,
                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                backdropFilter: 'blur(8px)',
                                                color: '#ffffff',
                                                width: 32,
                                                height: 32,
                                                zIndex: 3,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,115,81,0.9)',
                                                },
                                            }}>
                                            <X size={16} />
                                        </IconButton>
                                    )}

                                    <PhotoView src={imageUrl}>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                minHeight: { xs: 200, md: '100%' },
                                                backgroundImage: `url(${imageUrl})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                cursor: 'pointer',
                                                transition: 'transform 0.5s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.03)',
                                                },
                                            }}
                                        />
                                    </PhotoView>
                                </Box>
                            ))}
                        </Box>
                    </PhotoProvider>
                )}
            </Box>
        </Box>
    );
};

export { CompanyGalleryEdit };
